import { Box, useTheme } from '@mui/material';
import noop from 'lodash/noop';
import { useCallback } from 'react';

import { FooterItems } from 'layout/MainLayout/components/FooterItems';
import { StudentSection } from 'layout/MainLayout/components/Sidebar/components/components/StudentSection';
import { UserSection } from 'layout/MainLayout/components/Sidebar/components/components/UserSection';
import { UserSectionWrapper } from 'layout/MainLayout/components/Sidebar/components/components/UserSectionWrapper';
import { useParentExtraNavigationItems } from 'layout/MainLayout/components/Sidebar/hooks';
import { sidebarActions } from 'layout/MainLayout/components/Sidebar/store';
import { useAppDispatch, useAppSelector } from 'shared/hooks/store';
import { useUserRole } from 'shared/hooks/user';
import { UserRole } from 'shared/types';

import { CloseButton } from './components/CloseButton';
import { FooterWrapper } from './components/FooterWrapper';
import { MobileDrawer } from './components/MobileDrawer';
import { NavigationSection } from './components/NavigationSection';
import { SchoolSection } from './components/SchoolSection';
import { getDrawerStyles } from './utils';

import type { NavigationItem } from 'layout/MainLayout/components/Sidebar/types';
import type { FC } from 'react';
import type { AnalyticsEvent } from 'shared/constants/analytics';

interface SidebarMobileProps {
  navigationItems: NavigationItem[];
  onActive?(event: AnalyticsEvent): void;
}

export const SIDEBAR_MOBILE_TEST_ID = 'sidebar-mobile';

export const SidebarMobile: FC<SidebarMobileProps> = ({
  navigationItems,
  onActive = noop,
}) => {
  const role = useUserRole();
  const dispatch = useAppDispatch();
  const { opened } = useAppSelector(state => state.sidebar);
  const theme = useTheme();
  const parentExtraNavigationItems = useParentExtraNavigationItems();

  const closeSidebar = useCallback(() => {
    dispatch(sidebarActions.setSidebarState(false));
  }, [dispatch]);

  const openSidebar = useCallback(() => {
    dispatch(sidebarActions.setSidebarState(true));
  }, [dispatch]);

  const handleActive = (analyticsEvent: AnalyticsEvent) => {
    closeSidebar();
    onActive(analyticsEvent);
  };

  return (
    <MobileDrawer
      data-testid={SIDEBAR_MOBILE_TEST_ID}
      open={opened}
      onClose={closeSidebar}
      onOpen={openSidebar}
      sx={getDrawerStyles(opened || false, theme)}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <SchoolSection onClick={closeSidebar} />
        <CloseButton onClick={closeSidebar} />
      </Box>
      <StudentSection opened={opened} />
      <NavigationSection
        opened={opened}
        navigationItems={navigationItems}
        onActive={handleActive}
      />
      {role === UserRole.Parent && (
        <UserSectionWrapper opened={opened}>
          <UserSection />
          {parentExtraNavigationItems && (
            <NavigationSection
              opened={opened}
              navigationItems={parentExtraNavigationItems}
              onActive={handleActive}
            />
          )}
        </UserSectionWrapper>
      )}
      <FooterWrapper>
        <FooterItems onClick={closeSidebar} />
      </FooterWrapper>
    </MobileDrawer>
  );
};
